import { Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { create } from 'zustand'

import type { Profession } from 'src/shared/features/users/schemas/profession'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import type { Referral, ReferralData } from 'src/shared/schemas/referrals'

type ReferralMode = 'new' | 'newProfession' | 'repeatProfession'

interface SendReferralStore {
  referralFormData: ReferralData
  selectedReferral: Referral | null
  currentSendReferralStep: number // 0: select referral, 1: client info, 2: property info, 3: profession specific info, 4: confirm details
  error: string | null
  receiver: PublicUserData | null

  setReferralData: (stepData: ReferralData) => void
  setSelectedReferral: (referral: Referral | null) => void
  setCurrentSendReferralStep: (step: number) => void
  setError: (error: string | null) => void
  setReceiver: (receiver: PublicUserData | null) => void

  handleNext: (sendReferralStepData: ReferralData) => void
  handleBack: () => void
  handleSelectReferral: (referral: Referral) => void
  handleStartNewReferral: () => void
  resetSendReferralStore: () => void
  getReferralMode: () => ReferralMode
  getHeader: () => {
    title: string
    description: string | ReactNode
  }
  clearReceiver: () => void
}

export const useSendReferralStore = create<SendReferralStore>()((set, get) => ({
  referralFormData: {},
  selectedReferral: null,
  currentSendReferralStep: 0,
  error: null,
  receiver: null,

  setReferralData: (stepData: ReferralData) => set({ referralFormData: stepData }),
  setSelectedReferral: (referral: Referral | null) => set({ selectedReferral: referral }),
  setCurrentSendReferralStep: (step) => set({ currentSendReferralStep: step }),
  setError: (error) => set({ error }),
  setReceiver: (receiver: PublicUserData | null) => set({ receiver }),

  handleNext: (sendReferralStepData) => {
    const { referralFormData, currentSendReferralStep, setReferralData, setCurrentSendReferralStep } = get()
    const updatedReferralData = { ...referralFormData, ...sendReferralStepData }
    setReferralData(updatedReferralData)
    set({ error: null })
    setCurrentSendReferralStep(currentSendReferralStep + 1)
  },

  handleBack: () => {
    const { currentSendReferralStep, setCurrentSendReferralStep, setSelectedReferral, getReferralMode } = get()
    if (currentSendReferralStep > 0) {
      switch (getReferralMode()) {
        case 'new':
          if (currentSendReferralStep == 1) {
            setSelectedReferral(null)
            setCurrentSendReferralStep(0)
          } else {
            setCurrentSendReferralStep(currentSendReferralStep - 1)
          }
          break
        case 'newProfession':
          if (currentSendReferralStep < 4) {
            setSelectedReferral(null)
            setCurrentSendReferralStep(0)
          } else {
            setCurrentSendReferralStep(currentSendReferralStep - 1)
          }
          break
        case 'repeatProfession':
          if (currentSendReferralStep < 4) {
            setSelectedReferral(null)
            setCurrentSendReferralStep(0)
          } else {
            setCurrentSendReferralStep(currentSendReferralStep - 1)
          }
          break
      }
    }
  },

  handleSelectReferral: (referral: Referral) => {
    const { setSelectedReferral, setReferralData, setCurrentSendReferralStep, getReferralMode } = get()
    const {
      consumerId,
      clientFirstName,
      clientLastName,
      clientEmail,
      clientPhone,
      propertyType,
      desiredLocations,
      propertyCity,
      propertyState,
      propertyZip,
      propertyStreetAddress1,
      propertyStreetAddress2,
    } = referral

    setSelectedReferral(referral)
    setReferralData({
      // Include consumerId if it exists
      ...(consumerId ? { consumerId } : {}),
      clientFirstName,
      clientLastName,
      clientEmail,
      clientPhone,
      ...(desiredLocations ? { desiredLocations } : {}),
      ...(propertyType ? { propertyType } : {}),
      ...(propertyCity ? { propertyCity } : {}),
      ...(propertyState ? { propertyState } : {}),
      ...(propertyZip ? { propertyZip } : {}),
      ...(propertyStreetAddress1 ? { propertyStreetAddress1 } : {}),
      ...(propertyStreetAddress2 ? { propertyStreetAddress2 } : {}),
    })

    switch (getReferralMode()) {
      case 'new':
        setCurrentSendReferralStep(1)
        break
      case 'newProfession':
        setCurrentSendReferralStep(3)
        break
      case 'repeatProfession':
        setCurrentSendReferralStep(3)
        break
    }
  },

  handleStartNewReferral: () => {
    const { setSelectedReferral, setReferralData, setCurrentSendReferralStep } = get()
    setSelectedReferral(null)
    setReferralData({})
    setCurrentSendReferralStep(1)
  },

  resetSendReferralStore: () => {
    set({
      referralFormData: {},
      selectedReferral: null,
      currentSendReferralStep: 0,
      error: null,
      receiver: null,
    })
  },

  getReferralMode: () => {
    const { selectedReferral, receiver } = get()

    if (!receiver) {
      throw new Error('Receiver is required')
    }

    if (!selectedReferral || !selectedReferral.referredProfessions) {
      return 'new'
    }

    if (selectedReferral.referredProfessions.length === 0) {
      return 'new'
    }

    if (!selectedReferral.referredProfessions.includes(receiver.profession as Profession)) {
      return 'newProfession'
    }

    return 'repeatProfession'
  },

  getHeader: () => {
    const { currentSendReferralStep } = get()
    switch (currentSendReferralStep) {
      case 0:
        return { title: 'Select referral', description: 'Send a previously created referral or start a new one.' }
      case 1:
        return { title: 'Enter client info', description: 'This info remains private until you release it.' }
      case 2:
        return {
          title: 'Enter location',
          description: 'Select an option to indicate the area for the person handling your referral.',
        }
      case 3:
        return {
          title: 'Enter referral info',
          description: "You're just one step away from reviewing the referral details and sending it off!",
        }
      case 4:
        return {
          title: 'Please confirm info',
          description: InfoMessage(
            'This information will stay private until you release the referral in your "Sent Referrals" tab, even after you click "Confirm & Send".',
          ),
        }
      default:
        return {
          title: 'Send Referral',
          description: 'Complete the form to send your referral.',
        }
    }
  },

  clearReceiver: () => {
    set({ receiver: null })
  },
}))

const InfoMessage = (message: string) => (
  <div className="flex items-start gap-2.5">
    <Info className="mt-0.5 size-8 text-primary" />
    <p className="text-sm text-primary">{message}</p>
  </div>
)
